import React from "react";
import { WifiOff, RefreshCw, ShieldCheck, Cloud } from "lucide-react";
import PhoneFrame from "./PhoneFrame";
import { LANDING } from "@/constants/testIds";
import { MOCKUPS } from "@/constants/mockups";

const KnowItem = ({ k, v }) => (
  <div className="flex items-baseline gap-3">
    <span className="font-display text-2xl font-semibold text-[#0E6B6B]">{k}</span>
    <span className="text-[15px] text-[#3a4a4a]">{v}</span>
  </div>
);

export const SolutionBento = () => {
  return (
    <section
      id="features"
      data-testid={LANDING.features}
      className="bg-white py-24 md:py-32"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        <div className="max-w-3xl">
          <span className="text-xs md:text-sm tracking-[0.2em] uppercase font-bold text-[#D96C4A]">One record, built as the day happens</span>
          <h2 className="mt-4 font-display text-3xl md:text-4xl font-semibold leading-[1.1] text-[#0A2424] tracking-tight">
            Capture the day once. <span className="italic text-[#0E6B6B]">Use the truth everywhere.</span>
          </h2>
          <p className="mt-5 text-base md:text-lg text-[#3a4a4a]">
            Mobile workflows record work where it happens. The web workspace turns the same live operation into answers owners and managers can act on.
          </p>
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-y-3 gap-x-6">
            <KnowItem k="01" v="Know what is selling." />
            <KnowItem k="02" v="Know what is missing." />
            <KnowItem k="03" v="Know who owes you." />
            <KnowItem k="04" v="Know when to restock." />
          </div>
        </div>

        {/* Bento grid */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-6 gap-5 auto-rows-[minmax(260px,auto)]">

          {/* Offline-first — tall hero card */}
          <article
            id="offline-first"
            data-testid={LANDING.offlineFirst}
            className="md:col-span-4 md:row-span-2 relative overflow-hidden rounded-3xl bg-[#0A3B3B] text-white p-8 md:p-12 flex flex-col justify-between"
          >
            <div className="absolute inset-0 bg-kente opacity-[0.18] pointer-events-none" />
            <div className="absolute -right-20 -bottom-20 h-80 w-80 rounded-full bg-[#D96C4A]/20 blur-3xl pointer-events-none" />
            <div className="relative">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 text-xs font-semibold backdrop-blur">
                <WifiOff className="h-3.5 w-3.5" /> Offline-first
              </div>
              <h3 className="mt-6 font-display text-2xl md:text-4xl font-semibold leading-[1.1]">
                Keep selling even when the network <span className="italic text-[#FFD8A8]">fails</span>.
              </h3>
              <p className="mt-4 text-base text-white/80 max-w-xl leading-relaxed">
                Core sales, stock, and customer-credit work is written to local storage first. When the connection returns, queued operations retry automatically and conflicts are surfaced instead of being silently ignored.
              </p>
              <div className="mt-8 grid grid-cols-3 gap-4 max-w-md">
                <div className="rounded-2xl bg-white/8 p-4">
                  <Cloud className="h-5 w-5 text-[#FFD8A8] mb-2" />
                  <div className="text-xs text-white/70">Local-first</div>
                  <div className="font-display text-lg">Storage</div>
                </div>
                <div className="rounded-2xl bg-white/8 p-4">
                  <RefreshCw className="h-5 w-5 text-[#FFD8A8] mb-2" />
                  <div className="text-xs text-white/70">Auto retry</div>
                  <div className="font-display text-lg">Sync queue</div>
                </div>
                <div className="rounded-2xl bg-white/8 p-4">
                  <ShieldCheck className="h-5 w-5 text-[#FFD8A8] mb-2" />
                  <div className="text-xs text-white/70">Encrypted</div>
                  <div className="font-display text-lg">Sessions</div>
                </div>
              </div>
            </div>
          </article>

          {/* Analytics — featured with mockup */}
          <article
            id="analytics"
            data-testid={LANDING.analytics}
            className="md:col-span-2 md:row-span-2 relative overflow-hidden rounded-3xl bg-[#F7FAF9] border border-[#0E6B6B]/10 p-6 flex flex-col"
          >
            <span className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#D96C4A]">Analytics</span>
            <h3 className="mt-2 font-display text-2xl md:text-3xl font-semibold text-[#0A2424] leading-tight">
              See pressure before it becomes a problem.
            </h3>
            <p className="mt-2 text-sm text-[#4B5E5E]">
              Sales trends, fast and slow movers, restock recommendations, buying patterns, and demand forecasts from the records you already create.
            </p>
            <div className="mt-6 mx-auto w-[182px] self-center">
              <PhoneFrame src={MOCKUPS.dashboard} alt="Sales analytics dashboard" compact />
            </div>
          </article>

          {/* Sell — wide card */}
          <article className="md:col-span-3 relative overflow-hidden rounded-3xl bg-[#F7FAF9] border border-[#0E6B6B]/10 p-8 flex items-center gap-6">
            <div className="flex-1">
              <span className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#D96C4A]">Sell-first workspace</span>
              <h3 className="mt-2 font-display text-2xl md:text-3xl font-semibold text-[#0A2424] leading-tight">
                Record a sale in seconds.
              </h3>
              <p className="mt-3 text-[15px] text-[#4B5E5E]">
                Search by name, scan a barcode, build a cart and complete the sale — even when offline. Mark as <span className="text-[#D96C4A] font-semibold">sold on credit</span> in one tap.
              </p>
            </div>
            <div className="hidden sm:block w-[138px] flex-none self-end">
              <PhoneFrame src={MOCKUPS.sales} alt="Sell point of sale screen" compact />
            </div>
          </article>

          {/* Debt tracker */}
          <article
            id="debt"
            data-testid={LANDING.debt}
            className="md:col-span-3 relative overflow-hidden rounded-3xl bg-[#FFF6EE] border border-[#D96C4A]/20 p-8"
          >
            <span className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#D96C4A]">Debt tracker</span>
            <h3 className="mt-2 font-display text-2xl md:text-3xl font-semibold text-[#0A2424] leading-tight">
              Know exactly who owes you — and follow up faster.
            </h3>
            <p className="mt-3 text-[15px] text-[#4B5E5E] max-w-md">
                Keep the original sale, customer, amount, payments, due date, and remaining balance connected from first entry to final repayment.
            </p>
            <div className="mt-5 grid grid-cols-3 gap-3 max-w-sm">
              <div className="rounded-2xl bg-white p-3 border border-[#D96C4A]/15">
                <div className="text-xs text-[#4B5E5E]">Unpaid</div>
                <div className="font-display text-lg font-semibold text-[#D96C4A]">184k</div>
              </div>
              <div className="rounded-2xl bg-white p-3 border border-[#0E6B6B]/10">
                <div className="text-xs text-[#4B5E5E]">Customers</div>
                <div className="font-display text-lg font-semibold text-[#0E6B6B]">27</div>
              </div>
              <div className="rounded-2xl bg-white p-3 border border-[#0E6B6B]/10">
                <div className="text-xs text-[#4B5E5E]">Due today</div>
                <div className="font-display text-lg font-semibold text-[#0A2424]">6</div>
              </div>
            </div>
            <p className="mt-2 text-[11px] text-[#4B5E5E]">Illustrative balance snapshot</p>
            <div className="mt-6 hidden sm:flex justify-end">
              <div className="w-[138px]">
              <PhoneFrame src={MOCKUPS.debtTracker} alt="Debt tracker screen" compact />
              </div>
            </div>
          </article>

          {/* Inventory */}
          <article className="md:col-span-3 relative overflow-hidden rounded-3xl bg-white border border-[#0E6B6B]/10 p-8 flex items-center gap-6">
            <div className="flex-1">
              <span className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#D96C4A]">Inventory</span>
              <h3 className="mt-2 font-display text-2xl md:text-3xl font-semibold text-[#0A2424] leading-tight">
                Restock with confidence.
              </h3>
              <p className="mt-3 text-[15px] text-[#4B5E5E]">
                Track stock movement, catch low and out-of-stock items, scan barcodes, and use recent demand to decide what deserves cash first.
              </p>
            </div>
            <div className="hidden sm:block w-[138px] flex-none self-end">
              <PhoneFrame src={MOCKUPS.inventory} alt="Inventory management screen" compact />
            </div>
          </article>

          {/* Staff & branches */}
          <article
            id="staff-branch"
            data-testid={LANDING.staffBranch}
            className="md:col-span-3 relative overflow-hidden rounded-3xl bg-white border border-[#0E6B6B]/10 p-8 flex items-center gap-6"
          >
            <div className="flex-1">
              <span className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#D96C4A]">Staff & branch control</span>
              <h3 className="mt-2 font-display text-2xl md:text-3xl font-semibold text-[#0A2424] leading-tight">
                Give staff access without losing control.
              </h3>
              <p className="mt-3 text-[15px] text-[#4B5E5E]">
                Scope work by branch, assign role-based permissions, and keep operational activity attributed to the person who performed it.
              </p>
            </div>
            <div className="hidden sm:block w-[138px] flex-none self-end">
              <PhoneFrame src={MOCKUPS.staffAccess} alt="Staff access screen" compact />
            </div>
          </article>

        </div>
      </div>
    </section>
  );
};

export default SolutionBento;
